import React, { forwardRef, ReactNode, useEffect, useImperativeHandle, useMemo } from 'react';
import {
  Button,
  type ButtonProps,
  IconButton,
  Menu as MuiMenu,
  menuClasses,
  MenuList,
  type MenuProps,
  paperClasses,
  styled
} from '@mui/material';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { mergeSx } from 'merge-sx';

import EllipsisVerticalIcon from '@/assets/icons/ellipsis-vertical.svg?react';

const Menu = styled((props: MenuProps) => <MuiMenu {...props} />)(({ theme }) => ({
  [`& .${paperClasses.root}`]: {
    backgroundColor: theme.palette.background.default,
    boxShadow: 'box-shadow: 0px 0px 2px 0px #0000001F; box-shadow: 0px 4px 8px 0px #00000024;',

    [`& .${menuClasses.list}`]: {
      padding: 0
    }
  }
}));

// eslint-disable-next-line react-refresh/only-export-components
export enum DropdownTriggerMode {
  ICON_BUTTON = 'ICON_BUTTON',
  TEXT_BUTTON = 'TEXT_BUTTON'
}

interface DropdownMenuProps {
  children: ReactNode;
  onToggle?: (isOpen: boolean) => void;
  menuProps?: Pick<MenuProps, 'anchorOrigin' | 'transformOrigin'>;
  buttonProps?: Pick<ButtonProps, 'sx' | 'variant' | 'startIcon' | 'disabled'>;
  autoClose?: boolean;
  triggerMode?: DropdownTriggerMode;
  title?: string;
  customIcon?: ReactNode;
}

const DropdownMenu = forwardRef(
  (
    {
      children,
      onToggle,
      menuProps,
      buttonProps,
      autoClose,
      triggerMode = DropdownTriggerMode.ICON_BUTTON,
      title,
      customIcon
    }: DropdownMenuProps,
    ref
  ) => {
    const uniqueId = useMemo(() => `popover-${crypto.randomUUID()}`, []);
    const popupState = usePopupState({ variant: 'popover', popupId: uniqueId });
    const { anchorOrigin, transformOrigin } = menuProps || {};
    const { sx: buttonSx } = buttonProps || {};

    useImperativeHandle(
      ref,
      () => ({
        close: popupState.close,
        open: popupState.open,
        toggle: popupState.toggle
      }),
      [popupState]
    );

    useEffect(() => {
      onToggle?.(popupState.isOpen);
    }, [popupState.isOpen, onToggle]);

    return (
      <>
        {triggerMode === DropdownTriggerMode.ICON_BUTTON && (
          <IconButton
            {...buttonProps}
            sx={mergeSx(
              {
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              },
              buttonSx
            )}
            {...bindTrigger(popupState)}
          >
            {customIcon || <EllipsisVerticalIcon />}
          </IconButton>
        )}
        {triggerMode === DropdownTriggerMode.TEXT_BUTTON && (
          <Button
            {...buttonProps}
            sx={mergeSx(
              {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              },
              buttonSx
            )}
            {...bindTrigger(popupState)}
          >
            {title}
          </Button>
        )}
        <Menu
          {...bindMenu(popupState)}
          className="DropdownMenu-root"
          sx={{
            '& .MuiMenu-list': {
              py: 0
            },
            '& .MuiPaper-root': {
              bgcolor: 'background.default',
              position: 'relative',
              mt: 1,
              width: '240px'
            }
          }}
          onClose={popupState.close}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          PaperProps={{
            onClick: autoClose && popupState.close
          }}
        >
          <MenuList disablePadding>{children}</MenuList>
        </Menu>
      </>
    );
  }
);

DropdownMenu.displayName = 'DropdownMenu';

export default DropdownMenu;
